// A1-107 (operator 2026-11-02, 'Test UK1.xlsx'): 6,356 credit notes on the
// refund-channel card and only "Decide together 50 of 6356" - a page at a
// time. The card's ONE channel control, like the rate pick: scope "all N
// rows on this card" (every open row, all pages, picked by the server) or
// "only the rows I tick". A credit note takes only the channels that fit it
// (the server's channels_for), so each channel says how many of the rows it
// fits; the rest are skipped, counted, and stay open on the card.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { assignRefundChannels } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { REFUND_CHANNEL_REASONS } from "../../lib/reviewReasonPresets";
import { CHANNEL_LABELS } from "./RefundChannelResolver";
import { STICKY_BAR } from "./FamilyBBulkResolver";

/** Per channel, how many of these rows it fits ({channel: n}). */
const fitsOf = (channelLists) => channelLists.reduce((acc, list) => {
  (list || []).forEach((c) => { acc[c] = (acc[c] || 0) + 1; });
  return acc;
}, {});

export const RefundChannelAction = ({
  calcId, flagType, channelAll, tickedIds = [], tickedChannels = [], active = [], disabled,
  onDone, pageCount = 0, onSelectPage = null, onClearSelection = null,
}) => {
  const { t } = useTranslation();
  const total = channelAll?.rows || 0;
  const [scope, setScope] = useState(total > 0 ? "all" : "ticked");
  const [channel, setChannel] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const picked = tickedIds.length;
  const all = scope === "all";
  const n = all ? total : picked;
  const fits = all
    ? Object.fromEntries((channelAll?.channels || []).map((c) => [c.channel, c.rows]))
    : fitsOf(tickedChannels);
  const activeSet = new Set(active);
  const options = Object.keys(fits).sort((a, b) => (activeSet.has(b) - activeSet.has(a)) || (fits[b] - fits[a]));
  const skip = channel ? n - (fits[channel] || 0) : 0;
  const cleaned = reason.trim();
  const valid = !!channel && n > 0 && (fits[channel] || 0) > 0 && cleaned.length >= 10 && !/[<>]/.test(cleaned);

  const label = (c) => {
    const base = CHANNEL_LABELS[c] || c;
    return fits[c] === n ? base
      : t("ui.review_grouped.rc_fits_some", { label: base, fit: fits[c], n,
        defaultValue: "{{label}} - fits {{fit}} of {{n}}" });
  };

  const submit = async () => {
    if (!valid || busy || disabled) return;
    setBusy(true); setErr(null);
    try {
      const r = await assignRefundChannels(calcId, {
        ...(all ? { all_open: true } : { txn_ids: tickedIds }),
        flag_type: flagType, channel, reason: cleaned,
      });
      setChannel(""); setReason(""); setPreset("");
      toast.success(t("ui.review_grouped.rc_toast_ok", { defaultValue: "Channel assigned — figures change at the next re-run" }), {
        description: t("ui.review_grouped.rc_toast_ok_bulk", { n: r.assigned, channel: CHANNEL_LABELS[channel] || channel,
          defaultValue: "{{n}} credit notes → {{channel}}" }),
      });
      if (r.skipped) {
        toast.warning(t("ui.review_grouped.rc_toast_skipped", { n: r.skipped,
          defaultValue: "{{n}} credit notes do not fit that channel. They stay on the card for another channel." }));
      }
      if (onDone) await onDone();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(t("ui.review_grouped.rc_toast_fail", { defaultValue: "Could not assign channel" }), { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const tp = "rc-scope";
  return (
    <div data-testid={`${tp}-action-${flagType}`}
         className={`mt-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-3 space-y-2 ${STICKY_BAR}`}>
      <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
        {t("ui.review_grouped.rc_scope_title", { defaultValue: "Which channel do these credit notes belong to?" })}
      </div>
      <div data-testid={`${tp}-choice`} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-800 dark:text-slate-100">
        <label className="flex items-center gap-1.5">
          <input type="radio" name={`rc-scope-${flagType}`} checked={all} onChange={() => { setScope("all"); setChannel(""); }}
                 disabled={!total} data-testid={`${tp}-all`} />
          {t("ui.review_grouped.rc_scope_all", { n: total, defaultValue: "All {{n}} rows on this card" })}
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name={`rc-scope-${flagType}`} checked={!all} onChange={() => { setScope("ticked"); setChannel(""); }}
                 data-testid={`${tp}-ticked`} />
          {t("ui.review_grouped.rate_category.scope_ticked", { n: picked, defaultValue: "Only the rows I tick ({{n}})" })}
        </label>
        {!all && onSelectPage && (
          <button type="button" className="underline text-xs" data-testid={`${tp}-select-page`} onClick={onSelectPage}>
            {t("ui.review_grouped.bulk_b_select_all", { page: pageCount, defaultValue: "Select all {{page}} on this page" })}
          </button>
        )}
        {!all && picked > 0 && onClearSelection && (
          <button type="button" className="underline text-xs text-slate-500" onClick={onClearSelection}>
            {t("ui.review_grouped.bulk_b_clear", { defaultValue: "Clear" })}
          </button>
        )}
      </div>
      <select
        data-testid={`${tp}-channel`}
        value={channel}
        onChange={(e) => setChannel(e.target.value)}
        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs"
      >
        <option value="">{t("ui.review_grouped.rc_channel_placeholder", { defaultValue: "Select a channel…" })}</option>
        {options.map((c) => <option key={c} value={c}>{label(c)}</option>)}
      </select>
      {skip > 0 && (
        <p data-testid={`${tp}-skip`} className="text-xs text-slate-600 dark:text-slate-300">
          {t("ui.review_grouped.rc_scope_skip", { skip, n,
            defaultValue: "{{skip}} of the {{n}} rows do not fit this channel. They are left as they are and stay on the card." })}
        </p>
      )}
      <PresetReasonSelect testid={`${tp}-reason`} presets={REFUND_CHANNEL_REASONS} preset={preset}
                          onPick={(p, v) => { setPreset(p); setReason(v); }} />
      {err && <div data-testid={`${tp}-error`} className="text-xs text-red-700 dark:text-red-300">{err}</div>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {t("ui.review_grouped.rate_category.note", { defaultValue: "Records your answer — a calc re-run applies it. Your uploaded figures are not changed." })}
        </span>
        <button
          type="button"
          data-testid="rc-scope-apply"
          data-resolution-class="self-resolve"
          data-resolution-endpoint="POST /api/runs/calc/{calc_id}/assign-refund-channels"
          disabled={busy || !valid || disabled}
          onClick={submit}
          className="text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-700 dark:border-slate-300 text-slate-800 dark:text-slate-100 disabled:border-slate-300 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          {busy ? t("ui.review_grouped.rc_submitting", { defaultValue: "Saving…" })
            : t("ui.review_grouped.rc_scope_apply", { n: channel ? (fits[channel] || 0) : n,
              defaultValue: "Assign to {{n}} rows" })}
        </button>
      </div>
    </div>
  );
};

export default RefundChannelAction;
